import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "src/assets/imports/antdComponents";
import { ICitationReference, ITranscriptPreviewPopoverProps } from "../../models/interfaces";
import { useTranslation } from "react-i18next";
import { getOrder } from "../../transformer";
import { EllipsisText } from "src/components/EllipsisText";
import useIndexedDB from "src/components/useIndexDB";

const ALLOWED_IMAGE_CONTENT_TYPES = new Set(["image/png", "image/jpeg", "image/jpg", "image/webp"]);

const TranscriptPreviewPopover: React.FC<ITranscriptPreviewPopoverProps> = ({ references = [], onViewDetails }) => {
	const { t } = useTranslation();
	const [currentIndex, setCurrentIndex] = useState(0);
	const [localImageUrl, setLocalImageUrl] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const prevLocalUrlRef = useRef<string | null>(null);
	const abortRef = useRef<AbortController | null>(null);
	const { addOrUpdate, getItem } = useIndexedDB("reportCacheDB", "files");

	// Stash hook functions in a ref — useIndexedDB returns new references on every render,
	// which would otherwise cause the image-loading effect to loop infinitely.
	const dbRef = useRef({ addOrUpdate, getItem });
	useEffect(() => {
		dbRef.current = { addOrUpdate, getItem };
	}, [addOrUpdate, getItem]);

	const sortedReferences = useMemo(() => {
		if (!references || references.length === 0) return [];

		const hasOrderProperty = references.some((ref) => ref.order !== undefined && ref.order !== null);

		if (!hasOrderProperty) {
			return references;
		}

		return [...references].sort((a, b) => {
			return getOrder(a.order) - getOrder(b.order);
		});
	}, [references]);

	const currentRef: ICitationReference | undefined = sortedReferences[currentIndex];
	const total = sortedReferences.length;

	// A/V image lives under context_preview_image on the reference
	const avImagePath = currentRef?.context_preview_image?.image_path;
	const hasAvImage = !!(currentRef?.is_audio_video && avImagePath);

	const handleNext = (e: React.MouseEvent) => {
		e.stopPropagation();
		e.preventDefault();
		if (currentIndex < total - 1) setCurrentIndex(currentIndex + 1);
	};

	const handlePrev = (e: React.MouseEvent) => {
		e.stopPropagation();
		e.preventDefault();
		if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
	};

	const handleOpenDrawer = () => {
		if (currentRef) onViewDetails?.(currentRef);
	};

	// Fetch image when current A/V reference changes
	useEffect(() => {
		if (!hasAvImage || !avImagePath) {
			setLocalImageUrl(null);
			setIsLoading(false);
			setError(null);
			return;
		}

		abortRef.current?.abort();
		const controller = new AbortController();
		abortRef.current = controller;

		let cancelled = false;
		const cacheKey = `preview-av-${currentRef?.tr_id}-${currentRef?.first_dialog_no ?? "0"}`;
		const imageUrl = avImagePath;

		const loadImage = async () => {
			setIsLoading(true);
			setError(null);

			try {
				const { getItem: dbGet, addOrUpdate: dbPut } = dbRef.current;
				let blobUrl: string;

				let cachedBlob: Blob | null = null;
				try {
					const cachedFile = await dbGet(cacheKey);
					if (cachedFile?.fileBlob && ALLOWED_IMAGE_CONTENT_TYPES.has(cachedFile.fileBlob.type)) {
						cachedBlob = cachedFile.fileBlob;
					}
				} catch {
					// cache miss or read error — fall through to network
				}

				if (cancelled || controller.signal.aborted) return;

				if (cachedBlob) {
					blobUrl = window.URL.createObjectURL(cachedBlob);
				} else {
					const response = await fetch(imageUrl, {
						method: "GET",
						mode: "cors",
						cache: "no-cache",
						credentials: "omit",
						signal: controller.signal,
					});

					if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);

					const contentType = response.headers.get("Content-Type")?.split(";")[0]?.trim().toLowerCase();
					if (!contentType || !ALLOWED_IMAGE_CONTENT_TYPES.has(contentType)) {
						throw new Error(`Invalid content type received: ${contentType}. Expected an image type.`);
					}

					const blob = await response.blob();
					if (cancelled || controller.signal.aborted) return;

					try {
						await dbPut({ id: cacheKey, fileBlob: blob });
					} catch (cacheError) {
						console.error("Failed to cache image, continuing without cache:", cacheError);
					}

					blobUrl = window.URL.createObjectURL(blob);
				}

				if (cancelled || controller.signal.aborted) {
					window.URL.revokeObjectURL(blobUrl);
					return;
				}

				if (prevLocalUrlRef.current) {
					window.URL.revokeObjectURL(prevLocalUrlRef.current);
				}
				prevLocalUrlRef.current = blobUrl;
				setLocalImageUrl(blobUrl);
			} catch (err: any) {
				if (err?.name === "AbortError" || cancelled) return;
				console.error("Failed to load image:", err);
				setError("Failed to load image");
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};

		loadImage();

		return () => {
			cancelled = true;
			controller.abort();
		};
	}, [hasAvImage, avImagePath, currentRef?.tr_id, currentRef?.first_dialog_no]);

	// Final cleanup on unmount — revoke any lingering blob URLs
	useEffect(() => {
		return () => {
			if (prevLocalUrlRef.current) window.URL.revokeObjectURL(prevLocalUrlRef.current);
			abortRef.current?.abort();
		};
	}, []);

	if (sortedReferences.length === 0) return null;
	if (!currentRef) return null;

	return (
		<div className="bg-white shadow-xl rounded-xl overflow-hidden" style={{ width: "27.648rem" }}>
			{/* Sticky Header */}
			<div className={`flex items-center justify-between px-4 sticky top-0 bg-white ${total === 1 ? "pt-4" : "pt-3"}`}>
				<div className="flex-1 min-w-0 pr-3">
					<span className="text-sm font-semibold block truncate" style={{ color: "var(--secondary-color-2)" }}>
						{currentRef.file_name}
					</span>
				</div>

				{total > 1 && (
					<div className="flex items-center gap-1.5 flex-shrink-0">
						<button
							onClick={handlePrev}
							onMouseDown={(e) => e.preventDefault()}
							disabled={currentIndex === 0}
							className={`w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors
                ${currentIndex === 0 ? "opacity-30 cursor-not-allowed" : "cursor-pointer"}
              `}
							style={{ color: "var(--secondary-color)" }}>
							<i className="material-symbols-outlined text-base font-bold">chevron_backward</i>
						</button>
						<span className="text-sm font-semibold whitespace-nowrap px-1">
							<span style={{ color: "var(--secondary-color)" }}>{currentIndex + 1}</span>
							<span style={{ color: "#B1B1B5" }}>/{total}</span>
						</span>
						<button
							onClick={handleNext}
							onMouseDown={(e) => e.preventDefault()}
							disabled={currentIndex === total - 1}
							className={`w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors
                ${currentIndex === total - 1 ? "opacity-30 cursor-not-allowed" : "cursor-pointer"}
              `}
							style={{ color: "var(--secondary-color)" }}>
							<i className="material-symbols-outlined text-base font-bold">chevron_forward</i>
						</button>
					</div>
				)}
			</div>

			{/* Preview Content */}
			<div className="px-4 pt-3 pb-2">
				{/* Text preview — min-h only when no image follows */}
				<div className={`text-sm text-gray-700 leading-relaxed mb-3 ${hasAvImage ? "" : "min-h-[90px]"}`}>
					{typeof currentRef.context_preview === "string" ? currentRef.context_preview : currentRef.context_preview?.text || t("No preview available")}
				</div>

				{/* A/V image preview (only when is_audio_video + context_preview_image.image_path present) */}
				{hasAvImage && (
					<>
						{isLoading && <div className="mb-3 p-4 py-[5.2rem] text-center text-gray-500 border border-gray-200 rounded-lg">{t("Loading image...")}</div>}

						{error && <div className="mb-3 p-4 py-[5.2rem] text-center text-red-500 border border-red-200 rounded-lg">{error}</div>}

						{!error && !isLoading && localImageUrl && (
							<div
								className="mb-3 border border-gray-200 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center"
								style={{ width: "100%", height: "150px" }}>
								<img
									src={localImageUrl}
									alt={t("A/V preview")}
									style={{ maxWidth: "100%", maxHeight: "100%", width: "auto", height: "auto", objectFit: "contain" }}
								/>
							</div>
						)}
					</>
				)}

				{/* Footer */}
				<div className="flex justify-between items-center pt-2 pb-1">
					<span className="text-xs font-medium underline" style={{ color: "var(--secondary-color)" }}>
						{currentRef.entity_name && <EllipsisText text={currentRef.entity_name} />}
					</span>
					<Button type="link" onClick={handleOpenDrawer} className="font-semibold hover transition-colors p-0 h-auto text-sm" style={{ color: "var(--primary-color)" }}>
						{t("View Details")}
					</Button>
				</div>
			</div>
		</div>
	);
};

export default TranscriptPreviewPopover;
